import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "../../../components/ui-components/Toast";
import ScopeProjectsPicker from "./ScopeProjectsPicker";
import ValuePicker from "./ValuePicker";
import ScopeAxisSwitch from "./ScopeAxisSwitch";
import RefinementCard from "./RefinementCard";
import AddRefinementMenu from "./AddRefinementMenu";
import SourceReview from "./SourceReview";
import SourcePriorityPicker from "./SourcePriorityPicker";
import SourceLastModifiedLine from "./SourceLastModifiedLine";
import AdvancedCelEditor from "./AdvancedCelEditor";
import SourceCockpitHeader from "./SourceCockpitHeader";
import SourceConflictModal from "./SourceConflictModal";
import RecipeGallery from "./RecipeGallery";
import RecipeWizard from "./RecipeWizard";
import { createSource, updateSource, getSource, previewSource, getSourceValues, getMatrix, reorderSources, previewCleanupPlan, archiveSource } from "../api";
import { buildCreatePayload, buildCelPayload, classifiedToCriteria, hasConstraint, isWholeScope } from "../sourceCriteria";
import { recipeToInitial } from "../recipes";
import SourceTypePicker from "./SourceTypePicker";
import CoversToggle from "./CoversToggle";
import { getSourceTypes } from "../taxonomyApi";

const initialState = { scope: { projects: [], ai_workspaces: [] }, refinements: [], aiOnly: false };

const newUid = () => Math.random().toString(36).slice(2, 10);

const DefineSourcePage = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [advanced, setAdvanced] = useState(false);
  const [cel, setCel] = useState("");
  const [{ scope, refinements, aiOnly }, setSel] = useState(initialState);
  // Which scope axis this source uses: "project" (GCP) or "ai_workspace" (OpenAI/AI). The two
  // are disjoint — a cost row is keyed by project_id OR source_key='ai:<workspace>', never both —
  // so the builder commits to one axis at a time. ai workspaces are NOT a refinement of projects.
  const [scopeAxis, setScopeAxis] = useState("project");
  const [name, setName] = useState("");
  const [, setNameTouched] = useState(false);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(isEditMode);
  const [dirty, setDirty] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [sourceTypeId, setSourceTypeId] = useState("uncategorized");
  // Whole-scope mode: a single project/AI workspace, Any type, no refinements.
  const [wholeScope, setWholeScope] = useState(false);
  const [cleanupPlan, setCleanupPlan] = useState(null);
  const [cleanupSelected, setCleanupSelected] = useState([]);
  const previewGenRef = useRef(0);
  const cleanupGenRef = useRef(0);

  // Recipe gallery step machine: gallery → (params) → picker.
  // Edit mode starts directly on the picker so the existing hydrate flow is unchanged.
  const [step, setStep] = useState(isEditMode ? "picker" : "gallery");
  const [activeRecipe, setActiveRecipe] = useState(null);
  const [, setPresetSearch] = useState("");

  // Pre-fill from a deep link (e.g. the /allocation "uncovered cost" drawer's "Add source").
  // location.state.prefill = { dim, values, key?, name? } — start a fresh source already
  // matching that refinement, skipping the recipe gallery straight to the picker.
  useEffect(() => {
    if (isEditMode) return;
    const pf = location.state?.prefill;
    if (!pf || !pf.dim || !Array.isArray(pf.values) || pf.values.length === 0) return;
    const refinement = pf.dim === "label"
      ? { uid: newUid(), dim: "label", key: pf.key || "", values: pf.values }
      : { uid: newUid(), dim: pf.dim, values: pf.values };
    setSel({ scope: { projects: [], ai_workspaces: [] }, refinements: [refinement], aiOnly: false });
    setScopeAxis("project");
    if (pf.name) { setName(pf.name); setNameTouched(true); }
    setStep("picker");
    setDirty(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const criteria = useMemo(() => ({ scope, refinements }), [scope, refinements]);
  const aiWorkspaces = useMemo(() => scope.ai_workspaces || [], [scope.ai_workspaces]);
  // True when the user has selected nothing that constrains the source — drives the
  // "select something to preview" empty state and gates the live preview request.
  const scopeEmpty = scope.projects.length === 0 && aiWorkspaces.length === 0 && refinements.length === 0;
  const realType = !!sourceTypeId && sourceTypeId !== "uncategorized";
  const editSourceId = isEditMode ? Number(id) : null;

  const typesQ = useQuery(["taxonomy-source-types"], getSourceTypes);
  const typeName = useMemo(() => {
    if (!sourceTypeId || sourceTypeId === "uncategorized") return "";
    const t = (typesQ.data?.source_types || []).find((x) => x.source_type_id === sourceTypeId);
    return t?.display_name || "";
  }, [sourceTypeId, typesQ.data]);

  // Shares the query cache with ScopeProjectsPicker (same key) — costs nothing, gives us
  // the friendly names for project ids so the SourceReview statement can show "InstantViewAI"
  // instead of the raw UUID even when the live preview returns zero matched cost.
  const projectValuesQ = useQuery(
    ["source-values", "project", "", "", sourceTypeId, editSourceId ?? null],
    () => getSourceValues({ groupBy: "project", labelKey: "", search: "", limit: 100, source_type_id: sourceTypeId, excludeSourceId: editSourceId }),
    { enabled: !aiOnly && !advanced, keepPreviousData: true }
  );
  const projectLabels = useMemo(() => {
    const out = {};
    (projectValuesQ.data?.values || []).forEach((v) => { if (v.label && v.label !== v.value) out[v.value] = v.label; });
    return out;
  }, [projectValuesQ.data]);

  // Existing sources in priority order — only needed to PERSIST a chosen order on save (we
  // splice this source into the full list for /sources/reorder). The picker itself is driven
  // entirely by preview.overlaps, below.
  const { data: matrixData } = useQuery(["alloc-matrix"], () => getMatrix(), { keepPreviousData: true });
  const orderedSourceIds = useMemo(
    () => (matrixData?.sources || []).map((s) => s.id).filter((sid) => sid !== editSourceId),
    [matrixData, editSourceId]
  );

  // Priority only has a financial consequence where this source's criteria OVERLAP an existing
  // source — otherwise every cost row maps to exactly one source regardless of order. So the
  // control appears ONLY when there's overlap, and lists ONLY the contested sources (not the
  // whole catalogue). aheadIds = the contested sources that should claim before this one; null
  // means "untouched", so the backend keeps its default ranking.
  const overlaps = useMemo(() => preview?.overlaps || [], [preview]);
  const hasOverlap = overlaps.length > 0;
  const [aheadIds, setAheadIds] = useState(null);

  // Effective "claims ahead" set: the user's choice once they've touched it, else the backend's
  // current ranking (ranksAhead). Filtered to sources that are still contested.
  const aheadSet = useMemo(() => {
    if (aheadIds != null) return new Set(aheadIds.filter((id) => overlaps.some((o) => o.sourceId === id)));
    return new Set(overlaps.filter((o) => o.ranksAhead).map((o) => o.sourceId));
  }, [aheadIds, overlaps]);

  // Scoped picker rows: contested sources above "self" (claim first) then below, natural order.
  const priorityItems = useMemo(() => {
    const toItem = (o) => ({ key: o.sourceId, label: o.displayName, cost: o.monthlyCost });
    return [
      ...overlaps.filter((o) => aheadSet.has(o.sourceId)).map(toItem),
      { key: "self", label: name || "This source", isSelf: true },
      ...overlaps.filter((o) => !aheadSet.has(o.sourceId)).map(toItem),
    ];
  }, [overlaps, aheadSet, name]);

  // Drives the live preview: null until touched (backend heuristic), else the chosen ahead set.
  const rankAheadSourceIds = useMemo(
    () => (aheadIds == null ? null : [...aheadSet]),
    [aheadIds, aheadSet]
  );

  // Move "self" up (-1, claim earlier) / down (+1, claim later). Up drops the nearest source
  // that was ahead; down lets the nearest source below claim before it.
  const moveSelf = useCallback((dir) => {
    setAheadIds(() => {
      const ahead = overlaps.filter((o) => aheadSet.has(o.sourceId)).map((o) => o.sourceId);
      const behind = overlaps.filter((o) => !aheadSet.has(o.sourceId)).map((o) => o.sourceId);
      if (dir < 0 && ahead.length) ahead.pop();
      else if (dir > 0 && behind.length) ahead.push(behind.shift());
      return ahead;
    });
  }, [overlaps, aheadSet]);

  // Edit mode: load the source and hydrate criteria (or CEL) from it.
  useEffect(() => {
    if (!isEditMode) return undefined;
    let cancelled = false;
    setLoading(true);
    getSource(id)
      .then((res) => {
        if (cancelled) return;
        const src = res?.source ?? res;
        setName(src.display_name || "");
        setNameTouched(true);
        setSourceTypeId(src.source_type_id || "uncategorized");
        if (src.match_kind === "cel") {
          setAdvanced(true);
          setCel(src.match_expr || "");
          return;
        }
        const c = src.metadata?.criteria;
        if (c?.scope) {
          setSel({
            scope: { projects: c.scope.projects || [], ai_workspaces: c.scope.ai_workspaces || [] },
            refinements: (c.refinements || []).map((r) => ({ ...r, uid: newUid() })),
            aiOnly: false,
          });
          setScopeAxis((c.scope.ai_workspaces || []).length > 0 ? "ai_workspace" : "project");
          if (isWholeScope(c, src.source_type_id)) {
            setWholeScope(true);
          }
          return;
        }
        // Classified detected source — reconstruct a pick if it's project- or AI-workspace-scoped;
        // otherwise mark as not-editable in the v1 builder.
        if (src.match_kind === "classified") {
          const init = classifiedToCriteria(src.match_expr);
          if (init) {
            setSel({ ...init, aiOnly: false });
            setScopeAxis((init.scope.ai_workspaces || []).length > 0 ? "ai_workspace" : "project");
          } else {
            setSel({ scope: { projects: [], ai_workspaces: [] }, refinements: [], aiOnly: true });
          }
        }
      })
      .catch(() => {
        if (cancelled) return;
        toast.error("Source not found");
        navigate("/allocation");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, isEditMode, navigate]);

  // Debounced live preview: fires when scope/refinements change or a real type is selected.
  // previewLoading flips on the instant the inputs change (covers both the debounce window
  // and the in-flight backend call) so the right-rail can disclaim the stale value before
  // the new one lands — critical in a financial tool where a number must never silently lie.
  const [previewLoading, setPreviewLoading] = useState(false);
  useEffect(() => {
    if (advanced || aiOnly || ((scope.projects.length === 0 && aiWorkspaces.length === 0 && refinements.length === 0) && !realType)) {
      setPreview(null);
      setPreviewLoading(false);
      return undefined;
    }
    setPreviewLoading(true);
    const gen = ++previewGenRef.current;
    const t = setTimeout(async () => {
      try {
        const result = await previewSource(criteria, sourceTypeId, editSourceId, rankAheadSourceIds);
        if (gen === previewGenRef.current) {
          setPreview(result);
          setPreviewLoading(false);
        }
      } catch (e) {
        // Keep the last preview but stop the spinner — fall back to "stale but honest".
        if (gen === previewGenRef.current) setPreviewLoading(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [advanced, aiOnly, criteria, sourceTypeId, scope.projects.length, aiWorkspaces.length, refinements.length, realType, isEditMode, id, editSourceId, rankAheadSourceIds]);

  // Fetch the cleanup plan when in whole-scope mode with exactly one scope value selected.
  // Debounced like the preview effect. Pre-selects safe rows on resolution.
  useEffect(() => {
    const value =
      scopeAxis === "ai_workspace" ? (scope.ai_workspaces || [])[0] : (scope.projects || [])[0];
    if (!wholeScope || !value) {
      setCleanupPlan(null);
      setCleanupSelected([]);
      return undefined;
    }
    const gen = ++cleanupGenRef.current;
    const t = setTimeout(async () => {
      try {
        const res = await previewCleanupPlan(scopeAxis, value, editSourceId);
        if (gen !== cleanupGenRef.current) return;
        const covered = (res && res.covered) || [];
        setCleanupPlan({ covered });
        setCleanupSelected(covered.filter((c) => c.preselected).map((c) => c.sourceId));
      } catch (e) {
        if (gen !== cleanupGenRef.current) return;
        setCleanupPlan(null);
        setCleanupSelected([]);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [wholeScope, scopeAxis, scope.projects, scope.ai_workspaces, editSourceId]);

  // Warn before leaving with unsaved changes.
  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const markDirty = () => setDirty(true);

  // ── Scope / refinement mutators ───────────────────────────────────────────
  // Each mutator keeps the two scope axes mutually exclusive: touching projects clears any
  // AI workspaces and vice-versa, so the saved criteria only ever carry one populated axis.
  const setProjects = (projects) => { markDirty(); setSel((s) => ({ ...s, scope: { projects, ai_workspaces: [] } })); };
  const toggleAiWorkspace = (v) => {
    markDirty();
    setSel((s) => {
      const cur = s.scope.ai_workspaces || [];
      const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
      return { ...s, scope: { projects: [], ai_workspaces: next } };
    });
  };
  // Switch the scope axis. Clears the inactive axis's values; on the AI axis we also drop any
  // refinements (label/SKU/service apply only to project-keyed GCP cost, never OpenAI rows).
  const changeAxis = (axis) => {
    if (axis === scopeAxis) return;
    markDirty();
    setScopeAxis(axis);
    setSel((s) => axis === "ai_workspace"
      ? { ...s, scope: { projects: [], ai_workspaces: s.scope.ai_workspaces || [] }, refinements: [] }
      : { ...s, scope: { projects: s.scope.projects || [], ai_workspaces: [] } });
  };
  const addRefinement = (dim) => {
    markDirty();
    setSel((s) => ({
      ...s,
      refinements: [
        ...s.refinements,
        dim === "label" ? { uid: newUid(), dim, key: "", values: [] } : { uid: newUid(), dim, values: [] },
      ],
    }));
  };
  const updateRefinement = (idx, next) => {
    markDirty();
    setSel((s) => { const r = [...s.refinements]; r[idx] = next; return { ...s, refinements: r }; });
  };
  const removeRefinement = (idx) => {
    markDirty();
    setSel((s) => ({ ...s, refinements: s.refinements.filter((_, i) => i !== idx) }));
  };

  // Switching the Covers toggle. Entering whole-scope keeps the first selected
  // value (single-select); leaving it preserves the current scope as-is.
  const setCovers = (covers) => {
    markDirty();
    const whole = covers === "whole";
    setWholeScope(whole);
    if (whole) {
      setSourceTypeId("uncategorized");
      setSel((s) => {
        const projects = (s.scope.projects || []).slice(0, 1);
        const ai = (s.scope.ai_workspaces || []).slice(0, 1);
        return { ...s, scope: { projects, ai_workspaces: ai }, refinements: [] };
      });
    }
  };

  // Single-select project: replace selection with the newly-toggled value.
  const setProjectSingle = (nextProjects) => {
    markDirty();
    setSel((s) => {
      const added = nextProjects.find((p) => !(s.scope.projects || []).includes(p));
      return { ...s, scope: { projects: added ? [added] : [], ai_workspaces: [] } };
    });
  };

  // Single-select AI workspace: replace selection with the toggled value.
  const toggleAiWorkspaceSingle = (v) => {
    markDirty();
    setSel((s) => {
      const cur = s.scope.ai_workspaces || [];
      const next = cur.includes(v) ? [] : [v];
      return { ...s, scope: { projects: [], ai_workspaces: next } };
    });
  };

  const toggleCleanup = (sourceId) => {
    setCleanupSelected((sel) =>
      sel.includes(sourceId) ? sel.filter((x) => x !== sourceId) : [...sel, sourceId]
    );
  };

  // ── Recipe gallery handlers ──────────────────────────────────────────────
  const onSelectRecipe = (recipe) => {
    if (recipe.mode === "whole_scope") {
      markDirty();
      setWholeScope(true);
      setSourceTypeId("uncategorized");
      setSel({ scope: { projects: [], ai_workspaces: [] }, refinements: [], aiOnly: false });
      setScopeAxis("project");
      setStep("picker");
      return;
    }
    if (recipe.mode === "scratch") {
      setSel(initialState); setScopeAxis("project"); setPresetSearch(""); setAdvanced(false); setStep("picker"); return;
    }
    if (recipe.mode === "cel") {
      setSel(initialState); setScopeAxis("project"); setPresetSearch(""); setAdvanced(true); setStep("picker"); return;
    }
    const init = recipeToInitial(recipe);
    setSel({
      ...init.criteria,
      refinements: (init.criteria.refinements || []).map((r) => ({ ...r, uid: newUid() })),
      aiOnly: false,
    });
    setScopeAxis(init.scopeAxis || "project");
    setPresetSearch(init.presetSearch);
    setAdvanced(false);
    setActiveRecipe(recipe);
    setStep(init.paramSteps.length ? "params" : "picker");
  };

  const onWizardComplete = (c) => {
    setSel({
      ...c,
      refinements: (c.refinements || []).map((r) => ({ ...r, uid: r.uid || newUid() })),
      aiOnly: false,
    });
    setScopeAxis((c.scope?.ai_workspaces || []).length > 0 ? "ai_workspace" : "project");
    setStep("picker");
  };

  const backToRecipes = () => {
    setSel(initialState); setScopeAxis("project"); setAdvanced(false); setActiveRecipe(null); setPresetSearch(""); setStep("gallery");
    setWholeScope(false);
    setName("");
    setNameTouched(false);
    setDirty(false);
    setError(null);
  };

  const canSave = !aiOnly && name.trim() && (advanced ? cel.trim() : hasConstraint(criteria, sourceTypeId));
  const handleCancel = () => navigate("/allocation");

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true); setError(null); setConflict(null);
    const stripUid = ({ uid, ...rest }) => rest;
    const cleanCriteria = { ...criteria, refinements: criteria.refinements.map(stripUid) };
    const payload = advanced ? buildCelPayload(cel, name, sourceTypeId) : buildCreatePayload(cleanCriteria, name, sourceTypeId);
    try {
      let savedId = editSourceId;
      if (isEditMode) {
        await updateSource(id, payload);
        toast.success("Source updated");
        // Force the audit-log query to refetch so the "Last modified" line + drawer
        // reflect the change the user just made. Without this the cached entry stays,
        // the inline line shows stale info, and the save reads as "didn't work".
        queryClient.invalidateQueries(["source-history", Number(id)]);
      } else {
        const res = await createSource(payload);
        savedId = res?.source?.id ?? res?.id;
        toast.success("Source created");
        if (savedId != null) queryClient.invalidateQueries(["source-history", Number(savedId)]);
      }
      // Persist the chosen priority, if the user resolved an overlap. Splice this source into
      // the full priority order just behind the sources it should claim after, then write the
      // spaced order. The catch-all stays last (it's at the end of orderedSourceIds and the
      // splice only ever lands among real, contested sources). Best-effort: a reorder failure
      // must not lose the save itself.
      if (aheadIds != null && savedId != null) {
        const ahead = new Set(rankAheadSourceIds || []);
        const base = orderedSourceIds.filter((sid) => sid !== savedId);
        let insertAt = 0;
        base.forEach((sid, i) => { if (ahead.has(sid)) insertAt = i + 1; });
        const fullOrder = [...base.slice(0, insertAt), savedId, ...base.slice(insertAt)];
        try {
          await reorderSources(fullOrder);
        } catch (re) {
          toast.error("Source saved, but its priority order couldn't be updated.");
        }
      }
      setDirty(false);
      queryClient.invalidateQueries(["alloc-matrix"]);
      if (wholeScope && cleanupPlan && cleanupSelected.length > 0) {
        const toApply = cleanupPlan.covered.filter((c) => cleanupSelected.includes(c.sourceId));
        const axisKey = scopeAxis === "ai_workspace" ? "ai_workspaces" : "projects";
        for (const c of toApply) {
          try {
            if (c.action === "narrow") {
              const got = await getSource(c.sourceId);
              const s = got && got.source ? got.source : got;
              const cur = s && s.metadata ? s.metadata.criteria : null;
              if (!cur || !cur.scope) {
                toast.error(`Couldn't tidy up ${c.displayName}: source criteria unavailable.`);
                continue;
              }
              const narrowed = {
                ...cur,
                scope: { ...cur.scope, [axisKey]: (cur.scope[axisKey] || []).filter((v) => v !== c.narrowScopeValue) },
              };
              await updateSource(c.sourceId, buildCreatePayload(narrowed, s.display_name, s.source_type_id));
            } else {
              await archiveSource(c.sourceId);
            }
          } catch (err) {
            toast.error(`Couldn't tidy up ${c.displayName}.`);
          }
        }
        queryClient.invalidateQueries(["alloc-matrix"]);
        setCleanupPlan(null);
        setCleanupSelected([]);
      }
      if (!isEditMode) navigate(savedId != null ? `/allocation/sources/${savedId}` : "/allocation");
    } catch (e) {
      // Backend wraps the 409 as { error: { message, conflict: { id, display_name } } }.
      const conflictBody = e?.serverBody?.error?.conflict;
      if (e?.status === 409 && conflictBody) {
        setConflict(conflictBody);
      } else {
        // Surface the actual backend message + status so failures are diagnosable from the
        // UI. "Failed to update source" alone leaves the user (and us) guessing.
        const verb = isEditMode ? "update" : "create";
        const backendMsg = e?.serverBody?.error?.message || e?.message;
        const status = e?.status ? `${e.status} ` : "";
        setError(`Failed to ${verb} source — ${status}${backendMsg || "unknown error"}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const title = isEditMode ? (name || "Edit source") : "Define a cost source";

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-background-grey">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-text-3" />
      </div>
    );
  }

  // ── Gallery step ────────────────────────────────────────────────────────────
  if (step === "gallery") {
    return (
      <div className="h-full flex flex-col bg-background-grey">
        <SourceCockpitHeader title="Define a cost source" onCancel={handleCancel} onSave={handleSave} saveDisabled saving={saving} />
        <div className="flex-1 overflow-auto p-6">
          <RecipeGallery onSelect={onSelectRecipe} />
        </div>
      </div>
    );
  }

  // ── Params / wizard step ─────────────────────────────────────────────────────
  if (step === "params") {
    return (
      <div className="h-full flex flex-col bg-background-grey">
        <SourceCockpitHeader title="Define a cost source" onCancel={handleCancel} onSave={handleSave} saveDisabled saving={saving} />
        <div className="flex-1 overflow-auto p-6">
          <div className="bg-card rounded-card border border-card-bd p-5">
            <RecipeWizard recipe={activeRecipe} onComplete={onWizardComplete} onBack={backToRecipes} />
          </div>
        </div>
      </div>
    );
  }

  // ── Picker step (default / edit mode) ───────────────────────────────────────
  return (
    <div className="h-full flex flex-col bg-background-grey">
      <SourceCockpitHeader title={title} onCancel={handleCancel} onSave={handleSave} saveDisabled={!canSave} saving={saving} />

      <div className="flex-1 overflow-auto p-6">
        {/* Back to recipes link — only in new-source mode */}
        {!isEditMode && (
          <div className="mb-4">
            <button type="button" onClick={backToRecipes}
              className="text-xs text-text-2 hover:text-text underline">
              ← Back to recipes
            </button>
          </div>
        )}

        {/* Source name — finance identifies a source by its name. Standard full-width input, consistent with other detail pages. */}
        <div className="mb-6">
          <label htmlFor="source-name" className="block text-xs font-medium uppercase tracking-wide text-text-2 mb-1">Source name</label>
          <input
            id="source-name"
            aria-label="source name"
            value={name}
            onChange={(e) => { setNameTouched(true); markDirty(); setName(e.target.value); }}
            placeholder="Name this source"
            // w-full is dead in this project (the Tailwind config overrides the width scale),
            // so set full width inline to guarantee it.
            style={{ width: "100%" }}
            className="w-full border border-border rounded-ctrl px-3 py-2 text-base text-text focus:outline-none focus:ring-1 focus:ring-primary"
          />
          {/* Inline audit signal under the source name — answers "who last touched this?"
              in one line. Full timeline behind the "View history" link to keep the form
              clean. Edit mode only; create mode has no audit trail yet. */}
          <SourceLastModifiedLine sourceId={editSourceId} enabled={isEditMode} valueLabels={projectLabels} />
        </div>

        <div className="grid gap-8 items-start" style={{ gridTemplateColumns: "minmax(0, 1fr) 26rem" }}>
          {/* Left: inputs */}
          <div className="space-y-5 text-sm text-text bg-card rounded-card border border-card-bd p-5">
            <div className="pb-4 border-b border-border">
              <div className="mb-3">
                <CoversToggle value={wholeScope ? "whole" : "type"} onChange={setCovers} />
              </div>
              {wholeScope ? (
                <p className="text-xs text-text-2 mb-2">
                  <span className="font-medium text-text">Any type</span> — captures all cost in this scope not claimed by a more specific source.
                </p>
              ) : (
                <SourceTypePicker value={sourceTypeId} onChange={(v) => { markDirty(); setSourceTypeId(v); }} />
              )}
            </div>

            {aiOnly ? (
              <div className="text-sm text-text">
                This source is not editable in the new builder yet. Manage it from{" "}
                <a href="/allocation" className="underline text-primary">Detected Cost Sources</a>.
              </div>
            ) : advanced ? (
              <AdvancedCelEditor value={cel} onChange={(v) => { markDirty(); setCel(v); }} onBack={() => { markDirty(); setAdvanced(false); }} />
            ) : (
              <>
                {/* Scope axis — projects (GCP) vs AI workspaces (OpenAI) are disjoint cost
                    sets, so the source scopes ONE of them. The switch makes that explicit
                    instead of letting an AI workspace masquerade as a refinement of projects. */}
                <ScopeAxisSwitch value={scopeAxis} onChange={changeAxis} />

                {scopeAxis === "ai_workspace" ? (
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-text-2 mb-2">Scope · AI workspaces</p>
                    <ValuePicker
                      facet="ai_workspace"
                      selected={aiWorkspaces}
                      onToggle={wholeScope ? toggleAiWorkspaceSingle : toggleAiWorkspace}
                      initialSearch=""
                      sourceTypeId={sourceTypeId}
                      excludeSourceId={editSourceId}
                    />
                    <p className="mt-2 text-[11px] text-text-3">
                      AI workspaces capture OpenAI / AI-provider cost (keyed per workspace). Tag,
                      SKU and service refinements don&apos;t apply here — they only describe GCP cost.
                    </p>
                  </div>
                ) : (
                  <>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-text-2 mb-2">Scope · Projects</p>
                      <ScopeProjectsPicker projects={scope.projects} onChange={wholeScope ? setProjectSingle : setProjects} sourceTypeId={sourceTypeId} excludeSourceId={editSourceId} />
                    </div>
                    {!wholeScope && (
                      <div className="border-t border-border pt-4">
                        <p className="text-xs font-medium uppercase tracking-wide text-text-2 mb-2">Refinements</p>
                        {refinements.map((r, idx) => (
                          <RefinementCard key={r.uid} refinement={r}
                            onChange={(nr) => updateRefinement(idx, nr)}
                            onRemove={() => removeRefinement(idx)}
                            sourceTypeId={sourceTypeId} />
                        ))}
                        <AddRefinementMenu usedDims={refinements.map((r) => r.dim)} onAdd={addRefinement} />
                      </div>
                    )}
                  </>
                )}
              </>
            )}
            {!advanced && !aiOnly && !hasConstraint(criteria, sourceTypeId) && name.trim() && (
              <p className="text-xs text-text-2">
                {scopeAxis === "ai_workspace" ? "Pick a type or AI workspace to save." : wholeScope ? "Pick a project to save." : "Pick a type, project, or refinement to save."}
              </p>
            )}
            {error && <p className="text-xs text-danger">{error}</p>}
          </div>

          {/* Right: live preview rail */}
          <div className="sticky top-6 space-y-4">
            {aiOnly ? (
              <div className="rounded-card border border-card-bd bg-card px-4 py-3 text-xs text-text-3 italic">
                Preview is not available for detected sources.
              </div>
            ) : advanced ? (
              <div className="rounded-card border border-card-bd bg-card px-4 py-3 text-xs text-text-3 italic">
                Cost preview is not available for advanced expressions.
              </div>
            ) : (scopeEmpty && !realType) ? (
              <div className="rounded-card border border-card-bd bg-card px-4 py-3 text-xs text-text-3">
                Select a type or choose values to see matched cost and overlap.
              </div>
            ) : (
              <div className="rounded-card border border-card-bd bg-card p-4">
                <SourceReview criteria={criteria} preview={preview} typeName={typeName} sourceTypeId={sourceTypeId} excludeSourceId={editSourceId} valueLabels={projectLabels} loading={previewLoading} cleanupPlan={cleanupPlan} cleanupSelected={cleanupSelected} onToggleCleanup={toggleCleanup} />
                {/* Priority only matters where this source overlaps another (they'd contend for
                    the same cost). Show the resolver ONLY then, scoped to the contested sources —
                    moving "this source" recomputes the captured cost live and is saved as order.
                    No overlap → a quiet confirmation instead of a decision the user can't affect. */}
                {hasOverlap ? (
                  <div className="mt-6 border-t border-border pt-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-text-2 mb-1">Who claims the shared cost?</p>
                    <p className="text-[11px] text-text-2 mb-2">
                      Overlaps {overlaps.length} existing source{overlaps.length === 1 ? "" : "s"} — whoever is higher claims the shared cost first.
                    </p>
                    <SourcePriorityPicker items={priorityItems} onMove={moveSelf} />
                  </div>
                ) : preview ? (
                  <p className="mt-4 text-[11px] text-text-2">No overlap with existing sources — it captures all matching cost.</p>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>

      <SourceConflictModal
        open={conflict !== null}
        conflict={conflict}
        onOpenConflict={() => { if (conflict?.id != null) { setConflict(null); navigate(`/allocation/sources/${conflict.id}`); } }}
        onAdjust={() => setConflict(null)}
      />
    </div>
  );
};

export default DefineSourcePage;
